import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import axios from 'axios';
import { getSymbol } from '../utils/currency';
import VARIANT_CONFIG from '../config/variantConfig';
import {
  ArrowLeft, Heart, Share, Star, MapPin, Clock, Users, CaretLeft, CaretRight,
  Check, WifiHigh, Wind, Toilet, Shield, Camera as CameraIcon, Lightning, Car as CarIcon,
  Warning, ChatCircle, CalendarBlank, X, Bed, Tag, GridFour, Info, NavigationArrow
} from '@phosphor-icons/react';
import { HomeButton, BackButton } from '../components/NavButtons';
import SpaceMap from '../components/SpaceMap';
import CategoryBookingForm from '../components/CategoryBookingForm';
import { ListingMap } from '../components/MapComponents';
import AvailabilityCalendar from '../components/AvailabilityCalendar';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_LABELS = {
  parking: 'Parking', hotels: 'Hotel', gyms: 'Gym', hospital: 'Hospital',
  home: 'Home Space', coworking: 'Coworking', meeting_rooms: 'Meeting Room',
  event_spaces: 'Event Space', studios: 'Studio', storage: 'Storage',
  shops: 'Shop', malls: 'Mall Kiosk', halls: 'Hall', housing_societies: 'Society Space',
  religious_places: 'Religious Space', salons: 'Salon', clubs: 'Club',
  restaurants: 'Restaurant', sports: 'Sports Turf', ev_charging: 'EV Charging', education: 'Education',
  yoga_wellness: 'Yoga / Wellness'
};

const ListingDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isWishlisted, setIsWishlisted] = useState(false);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [blockedDates, setBlockedDates] = useState(new Set());
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [selectedService, setSelectedService] = useState(null);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [listingAds, setListingAds] = useState({ sidebar: [], below: [] });

  useEffect(() => {
    fetchListing();
    if (user) checkWishlist();
    Promise.all([
      axios.get(`${API_URL}/api/ads?position=listing_sidebar`).catch(() => ({ data: { ads: [] } })),
      axios.get(`${API_URL}/api/ads?position=listing_below`).catch(() => ({ data: { ads: [] } })),
    ]).then(([sideRes, belowRes]) => {
      setListingAds({ sidebar: sideRes.data.ads || [], below: belowRes.data.ads || [] });
    });
  }, [id, user]);

  const fetchListing = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/listings/${id}`);
      setListing(response.data);
    } catch (error) {
      console.error('Error fetching listing:', error);
    } finally {
      setLoading(false);
    }
  };

  const checkWishlist = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/wishlist`);
      setIsWishlisted(response.data.listings?.some(l => l.listing_id === id) || false);
    } catch (error) {
      console.error('Wishlist check error:', error);
    }
  };

  const toggleWishlist = async () => {
    if (!user) { navigate('/login'); return; }
    try {
      if (isWishlisted) {
        await axios.delete(`${API_URL}/api/wishlist/${id}`);
      } else {
        await axios.post(`${API_URL}/api/wishlist/${id}`, {});
      }
      setIsWishlisted(!isWishlisted);
    } catch (error) { console.error('Wishlist error:', error); }
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({ title: listing?.title, text: `Check out this space: ${listing?.title}`, url: window.location.href });
    } else {
      navigator.clipboard.writeText(window.location.href);
    }
  };

  const nextImage = () => {
    if (listing?.images) setCurrentImageIndex((prev) => (prev + 1) % listing.images.length);
  };
  const prevImage = () => {
    if (listing?.images) setCurrentImageIndex((prev) => (prev - 1 + listing.images.length) % listing.images.length);
  };

  const AMENITY_ICONS = {
    'Wi-Fi': WifiHigh, 'AC': Wind, 'Washroom': Toilet, 'Security': Shield,
    'CCTV': CameraIcon, 'Charging Points': Lightning, 'Parking': CarIcon
  };

  // Extract enabled variants from category_specific
  const variants = useMemo(() => {
    if (!listing?.category_specific?.variants) return [];
    return listing.category_specific.variants.filter(v => v.enabled !== false);
  }, [listing]);

  // Memoize the available services list so the .filter() doesn't rerun every render
  const availableServices = useMemo(
    () => (listing?.services || []).filter(s => s.available !== false),
    [listing?.services]
  );

  const variantConfig = listing?.category ? VARIANT_CONFIG[listing.category] : null;
  const cs = listing ? getSymbol(listing.currency || listing.country) : '';

  const scrollToBooking = () => {
    const el = document.getElementById('booking-sidebar');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    if (window.innerWidth < 1024) setShowBookingModal(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!listing) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-slate-700 mb-2">Listing not found</h2>
          <Button onClick={() => navigate('/buyer')} className="rounded-full">Go Home</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 pb-24">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/90 backdrop-blur-xl border-b border-slate-200/50">
        <div className="max-w-7xl mx-auto px-4 h-14 flex items-center justify-between">
          <div className="flex items-center gap-1">
            <BackButton />
            <HomeButton />
          </div>
          <div className="flex items-center gap-1.5">
            <button onClick={handleShare} className="p-2 hover:bg-slate-100 rounded-full" data-testid="share-btn">
              <Share size={20} className="text-slate-700" />
            </button>
            <button onClick={toggleWishlist} className="p-2 hover:bg-slate-100 rounded-full" data-testid="wishlist-btn">
              <Heart size={20} weight={isWishlisted ? 'fill' : 'regular'} className={isWishlisted ? 'text-red-500' : 'text-slate-700'} />
            </button>
          </div>
        </div>
      </header>

      {/* Image Gallery */}
      <div className="relative bg-slate-900 h-56 sm:h-72 lg:h-80">
        {listing.images && listing.images.length > 0 ? (
          <>
            <img src={listing.images[currentImageIndex]} alt={listing.title} className="w-full h-full object-cover" />
            {listing.images.length > 1 && (
              <>
                <button onClick={prevImage} className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 bg-white/80 rounded-full flex items-center justify-center hover:bg-white transition-colors" data-testid="prev-image-btn">
                  <CaretLeft size={18} />
                </button>
                <button onClick={nextImage} className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 bg-white/80 rounded-full flex items-center justify-center hover:bg-white transition-colors" data-testid="next-image-btn">
                  <CaretRight size={18} />
                </button>
                <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
                  {listing.images.map((_, i) => (
                    <button key={`img-dot-${i}`} onClick={() => setCurrentImageIndex(i)} className={`w-2 h-2 rounded-full transition-all ${i === currentImageIndex ? 'bg-white w-5' : 'bg-white/50'}`}></button>
                  ))}
                </div>
              </>
            )}
          </>
        ) : (
          <div className="w-full h-full bg-slate-200 flex items-center justify-center">
            <span className="text-slate-400 text-lg">No images</span>
          </div>
        )}
        <div className="absolute top-3 left-3 flex gap-2">
          {listing.instant_booking && (
            <span className="bg-emerald-500 text-white text-xs font-semibold px-3 py-1.5 rounded-full flex items-center gap-1" data-testid="instant-badge">
              <Lightning size={12} weight="fill" /> Instant Book
            </span>
          )}
          <span className="bg-white/90 text-slate-700 text-xs font-semibold px-3 py-1.5 rounded-full" data-testid="category-badge">
            {CATEGORY_LABELS[listing.category] || listing.category}
          </span>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-5">
        <div className="lg:grid lg:grid-cols-3 lg:gap-6">
          {/* ===== Main Content (Left 2/3) ===== */}
          <div className="lg:col-span-2 space-y-5">

            {/* 1. Title & Rating */}
            <div data-testid="listing-header">
              <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 leading-tight">{listing.title}</h1>
              <div className="flex flex-wrap items-center gap-3 mt-2">
                <div className="flex items-center gap-1 text-sm">
                  <Star size={16} weight="fill" className="text-amber-400" />
                  <span className="font-semibold text-slate-800">{listing.rating?.toFixed(1) || '0.0'}</span>
                  <span className="text-slate-400">({listing.review_count || 0})</span>
                </div>
                <div className="flex items-center gap-1 text-sm text-slate-500">
                  <MapPin size={14} weight="fill" />
                  {listing.latitude && listing.longitude ? (
                    <a
                      href={`https://www.google.com/maps/dir/?api=1&destination=${listing.latitude},${listing.longitude}`}
                      target="_blank" rel="noopener noreferrer"
                      className="line-clamp-1 hover:text-blue-600 hover:underline transition-colors"
                      data-testid="address-directions-link"
                      title="Open directions in Google Maps"
                    >{listing.address}</a>
                  ) : (
                    <span className="line-clamp-1">{listing.address}</span>
                  )}
                </div>
                {(listing.latitude && listing.longitude) && (
                  <a
                    href={`https://www.google.com/maps/dir/?api=1&destination=${listing.latitude},${listing.longitude}`}
                    target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-xs font-semibold text-white bg-gradient-to-br from-sky-500 via-blue-500 to-indigo-600 hover:from-sky-600 hover:via-blue-600 hover:to-indigo-700 px-3 py-1.5 rounded-full transition-all shadow-[0_2px_6px_-1px_rgba(59,130,246,0.4)] hover:-translate-y-0.5"
                    data-testid="get-directions-pill"
                    title="Get turn-by-turn directions from your current location"
                  >
                    <NavigationArrow size={12} weight="fill" /> Get Directions
                  </a>
                )}
                {listing.google_map_link && (
                  <a href={listing.google_map_link} target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-700 bg-blue-50 hover:bg-blue-100 px-2.5 py-1 rounded-full transition-colors"
                    data-testid="google-map-link">
                    <MapPin size={12} weight="fill" /> View on Google Maps
                  </a>
                )}
                <div className="flex items-center gap-1 text-sm">
                  <span className="text-2xl font-bold text-emerald-600">{cs}{listing.base_price}</span>
                  <span className="text-slate-400">/ {listing.pricing_model === 'hourly' ? 'hr' : listing.pricing_model}</span>
                </div>
              </div>
            </div>

            {/* Hosted By — pinned to the top so buyers can reach the host in one tap */}
            <div className="bg-white rounded-2xl p-4 border border-slate-100" data-testid="seller-section">
              <h2 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Hosted by</h2>
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-11 h-11 bg-emerald-100 rounded-full flex items-center justify-center shrink-0">
                    <span className="text-emerald-700 font-semibold text-lg">{listing.seller_name?.charAt(0)}</span>
                  </div>
                  <div className="min-w-0">
                    <p className="font-semibold text-slate-900 text-sm truncate">{listing.seller_name}</p>
                    <p className="text-xs text-slate-400">{listing.booking_count || 0} bookings</p>
                  </div>
                </div>
                <Button variant="outline" size="sm" className="rounded-full text-xs h-9"
                  onClick={() => {
                    const q = new URLSearchParams({
                      seller_id: listing.seller_id,
                      listing_id: listing.listing_id,
                    });
                    if (selectedService?.name) q.set('service', selectedService.name);
                    if (selectedVariant !== null && listing.category_specific?.variants?.[selectedVariant]) {
                      const v = listing.category_specific.variants[selectedVariant];
                      q.set('variant', v.name || v.title || `Option ${selectedVariant + 1}`);
                    }
                    navigate(`/messages?${q.toString()}`);
                  }}
                  data-testid="contact-seller-btn">
                  <ChatCircle size={14} className="mr-1.5" /> Contact
                </Button>
              </div>
            </div>

            {/* 2. About This Space */}
            <div className="bg-white rounded-2xl p-5 border border-slate-100" data-testid="about-section">
              <h2 className="text-base font-semibold text-slate-900 mb-2">About this space</h2>
              <p className="text-sm text-slate-600 leading-relaxed">{listing.description}</p>
              {listing.entry_instructions && (
                <div className="mt-3 flex items-start gap-2 text-sm text-slate-500 bg-slate-50 rounded-xl p-3">
                  <Info size={16} className="text-slate-400 mt-0.5 shrink-0" />
                  <div><span className="font-medium text-slate-600">Entry: </span>{listing.entry_instructions}</div>
                </div>
              )}
            </div>

            {/* Location Map */}
            {(listing.latitude && listing.longitude) && (
              <div className="bg-white rounded-2xl p-5 border border-slate-100" data-testid="map-section">
                <h2 className="text-base font-semibold text-slate-900 mb-3 flex items-center gap-2">
                  <MapPin size={18} weight="fill" className="text-blue-500" /> Location
                </h2>
                <ListingMap latitude={listing.latitude} longitude={listing.longitude} title={listing.title} address={listing.address} height="250px" />
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <a href={`https://www.google.com/maps/dir/?api=1&destination=${listing.latitude},${listing.longitude}`}
                    target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-sm font-semibold text-white bg-gradient-to-br from-sky-500 via-blue-500 to-indigo-600 hover:from-sky-600 hover:via-blue-600 hover:to-indigo-700 px-4 py-2 rounded-full transition-all shadow-[0_3px_8px_-2px_rgba(59,130,246,0.5)] hover:-translate-y-0.5"
                    data-testid="get-directions-btn">
                    <NavigationArrow size={14} weight="fill" /> Get Directions
                  </a>
                  <a href={`https://www.google.com/maps/search/?api=1&query=${listing.latitude},${listing.longitude}`}
                    target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-700 hover:text-slate-900 bg-white border border-slate-200 hover:border-slate-300 hover:bg-slate-50 px-4 py-2 rounded-full transition-all"
                    data-testid="view-on-map-btn">
                    <MapPin size={14} weight="fill" /> View on Map
                  </a>
                  {listing.google_map_link && (
                    <a href={listing.google_map_link} target="_blank" rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-600 hover:text-slate-700 bg-slate-50 hover:bg-slate-100 px-3 py-1.5 rounded-full transition-colors">
                      Open in Google Maps
                    </a>
                  )}
                </div>
              </div>
            )}

            {/* 3. Variant Options (Room Types / Plans etc.) */}
            {variants.length > 0 && variantConfig && (
              <div className="bg-white rounded-2xl p-5 border border-slate-100" data-testid="variants-section">
                <h2 className="text-base font-semibold text-slate-900 mb-3 flex items-center gap-2">
                  <Tag size={18} className="text-emerald-500" />
                  Options Available
                </h2>
                <div className="space-y-2">
                  {variants.map((variant, i) => {
                    const variantName = variant[variantConfig.variantField] || `Option ${i + 1}`;
                    const regPrice = parseFloat(variant.regular_price) || 0;
                    const holPrice = parseFloat(variant.holiday_price) || 0;
                    const isSelected = selectedVariant === i;

                    return (
                      <button key={`v-${variant.variant_name || ''}-${i}`} onClick={() => { setSelectedVariant(isSelected ? null : i); scrollToBooking(); }}
                        className={`w-full text-left p-3.5 rounded-xl border-2 transition-all ${isSelected ? 'border-emerald-500 bg-emerald-50' : 'border-slate-100 hover:border-slate-200 bg-white'}`}
                        data-testid={`variant-option-${i}`}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${isSelected ? 'bg-emerald-500' : 'bg-slate-100'}`}>
                              {isSelected ? <Check size={16} className="text-white" weight="bold" /> : <Bed size={16} className="text-slate-500" />}
                            </div>
                            <div>
                              <p className={`text-sm font-semibold ${isSelected ? 'text-emerald-800' : 'text-slate-800'}`}>{variantName}</p>
                              <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-0.5">
                                {variantConfig.columns.filter(col => {
                                  const val = variant[col.key];
                                  return val !== undefined && val !== '' && val !== false && val !== null;
                                }).slice(0, 4).map(col => (
                                  <span key={col.key} className="text-xs text-slate-400">
                                    {col.label}: <span className="text-slate-600 font-medium">{col.type === 'toggle' ? 'Yes' : variant[col.key]}</span>
                                  </span>
                                ))}
                              </div>
                            </div>
                          </div>
                          <div className="text-right shrink-0 ml-3">
                            <p className="text-sm font-bold text-slate-900">{cs}{regPrice.toLocaleString()}</p>
                            {holPrice > 0 && holPrice !== regPrice && (
                              <p className="text-[10px] text-orange-500 font-medium">Holiday: {cs}{holPrice.toLocaleString()}</p>
                            )}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* 4. Availability Calendar */}
            <div className="bg-white rounded-2xl p-5 border border-slate-100" data-testid="calendar-section">
              <AvailabilityCalendar listingId={id} onBlockedDatesLoaded={setBlockedDates} />
            </div>

            {/* 5. Space Availability Grid */}
            <div className="bg-white rounded-2xl p-5 border border-slate-100" data-testid="space-grid-section">
              <SpaceMap
                listingId={id}
                interactive={true}
                selectedUnitId={selectedUnit?.unit_id}
                onUnitSelect={(unit) => {
                  setSelectedUnit(unit);
                  scrollToBooking();
                }}
              />
              {selectedUnit && (
                <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-xl flex items-center justify-between" data-testid="selected-unit-badge">
                  <span className="text-sm font-medium text-blue-700">
                    Selected: <strong>{selectedUnit.label}</strong>
                  </span>
                  <button onClick={() => setSelectedUnit(null)} className="text-blue-500 hover:text-blue-700 p-1" data-testid="clear-unit-btn">
                    <X size={16} />
                  </button>
                </div>
              )}
            </div>

            {/* 6. Amenities */}
            {listing.amenities && listing.amenities.length > 0 && (
              <div className="bg-white rounded-2xl p-5 border border-slate-100" data-testid="amenities-section">
                <h2 className="text-base font-semibold text-slate-900 mb-3">Amenities</h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {listing.amenities.map((amenity, i) => {
                    const Icon = AMENITY_ICONS[amenity] || Check;
                    return (
                      <div key={`am-${amenity}-${i}`} className="flex items-center gap-2.5 text-sm text-slate-700">
                        <div className="w-8 h-8 bg-emerald-50 rounded-lg flex items-center justify-center shrink-0">
                          <Icon size={16} className="text-emerald-600" />
                        </div>
                        <span>{amenity}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* 7. Services */}
            {availableServices.length > 0 && (
              <div className="bg-white rounded-2xl p-5 border border-slate-100" data-testid="services-section">
                <h2 className="text-base font-semibold text-slate-900 mb-3">Services Available</h2>
                <div className="space-y-2">
                  {availableServices.map(svc => {
                    const isSelected = selectedService?.service_id === svc.service_id;
                    return (
                      <button key={svc.service_id}
                        onClick={() => {
                          setSelectedService(isSelected ? null : svc);
                          if (!isSelected) scrollToBooking();
                        }}
                        className={`w-full text-left p-3.5 rounded-xl border-2 transition-all flex items-center gap-3 ${isSelected ? 'border-emerald-500 bg-emerald-50' : 'border-slate-100 hover:border-slate-200 bg-white'}`}
                        data-testid={`service-select-${svc.service_id}`}>
                        <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${isSelected ? 'bg-emerald-500' : 'bg-slate-100'}`}>
                          {isSelected ? <Check size={16} className="text-white" weight="bold" /> :
                            <span className="text-xs font-bold text-slate-500">{cs}</span>}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-slate-900 text-sm">{svc.name}</p>
                          {svc.description && <p className="text-xs text-slate-500 line-clamp-1">{svc.description}</p>}
                          <div className="flex items-center gap-3 mt-0.5 text-xs text-slate-400">
                            {svc.duration_minutes && <span className="flex items-center gap-0.5"><Clock size={10} /> {svc.duration_minutes}min</span>}
                            {svc.capacity && <span className="flex items-center gap-0.5"><Users size={10} /> {svc.capacity}</span>}
                          </div>
                        </div>
                        <span className="font-bold text-slate-900 text-sm shrink-0">{cs}{svc.price}</span>
                      </button>
                    );
                  })}
                </div>
                {selectedService && (
                  <p className="text-xs text-emerald-600 mt-2 font-medium" data-testid="selected-service-note">
                    Selected: {selectedService.name} ({cs}{selectedService.price})
                  </p>
                )}
              </div>
            )}

            {/* 8. House Rules */}
            {listing.rules && listing.rules.length > 0 && (
              <div className="bg-white rounded-2xl p-5 border border-slate-100" data-testid="rules-section">
                <h2 className="text-base font-semibold text-slate-900 mb-3">House Rules</h2>
                <ul className="space-y-2">
                  {listing.rules.map((rule, i) => (
                    <li key={`rule-${rule}-${i}`} className="flex items-start gap-2.5 text-sm text-slate-600">
                      <Warning size={16} className="text-amber-500 flex-shrink-0 mt-0.5" />
                      <span>{rule}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* 9. Reviews */}
            <div className="bg-white rounded-2xl p-5 border border-slate-100" data-testid="reviews-section">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-base font-semibold text-slate-900">Reviews</h2>
                <div className="flex items-center gap-1 text-sm">
                  <Star size={14} weight="fill" className="text-amber-400" />
                  <span className="font-semibold">{listing.rating?.toFixed(1) || '0.0'}</span>
                  <span className="text-slate-400">({listing.review_count || 0})</span>
                </div>
              </div>
              {listing.reviews && listing.reviews.length > 0 ? (
                <div className="space-y-3">
                  {listing.reviews.map((review) => (
                    <div key={review.review_id} className="border-b border-slate-100 pb-3 last:border-0 last:pb-0">
                      <div className="flex items-center gap-2.5 mb-1.5">
                        <div className="w-8 h-8 bg-slate-100 rounded-full flex items-center justify-center">
                          {review.buyer_photo ? (
                            <img src={review.buyer_photo} alt={review.buyer_name} className="w-8 h-8 rounded-full object-cover" />
                          ) : (
                            <span className="text-slate-600 text-xs font-medium">{review.buyer_name?.charAt(0)}</span>
                          )}
                        </div>
                        <div>
                          <p className="font-medium text-slate-900 text-sm">{review.buyer_name}</p>
                          <div className="flex items-center gap-0.5">
                            {[...Array(5)].map((_, i) => (
                              <Star key={`star-${i}`} size={10} weight="fill" className={i < review.rating ? 'text-amber-400' : 'text-slate-200'} />
                            ))}
                          </div>
                        </div>
                      </div>
                      <p className="text-sm text-slate-600">{review.review_text}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-slate-400 text-center py-4 text-sm">No reviews yet</p>
              )}
            </div>

            {/* 10. Hosted By — moved to the top position (see below Title) */}
          </div>

          {/* ===== Booking Sidebar (Right 1/3) - Desktop ===== */}
          <div className="hidden lg:block">
            <div id="booking-sidebar" className="sticky top-20 space-y-4">
              <CategoryBookingForm
                listing={listing}
                blockedDates={blockedDates}
                selectedUnit={selectedUnit}
                onClearUnit={() => setSelectedUnit(null)}
                selectedService={selectedService}
                onClearService={() => setSelectedService(null)}
                selectedVariantIndex={selectedVariant}
              />
              {listingAds.sidebar.length > 0 && (
                <a href={listingAds.sidebar[0].link_url || '#'} target={listingAds.sidebar[0].link_url ? '_blank' : '_self'} rel="noopener noreferrer" className="block relative rounded-xl overflow-hidden" data-testid="listing-sidebar-ad">
                  <img src={listingAds.sidebar[0].media_url} alt={listingAds.sidebar[0].title} className="w-full rounded-xl object-cover" style={{ maxHeight: '260px' }} />
                  <span className="absolute top-1.5 left-1.5 bg-black/50 text-white text-[8px] font-bold px-1 py-0.5 rounded">Ad</span>
                </a>
              )}
            </div>
          </div>
        </div>

        {/* Below Details Ad */}
        {listingAds.below.length > 0 && (
          <div className="mt-4 relative rounded-xl overflow-hidden" data-testid="listing-below-ad">
            <a href={listingAds.below[0].link_url || '#'} target={listingAds.below[0].link_url ? '_blank' : '_self'} rel="noopener noreferrer" className="block">
              <img src={listingAds.below[0].media_url} alt={listingAds.below[0].title} className="w-full h-14 sm:h-18 object-cover rounded-xl" />
            </a>
            <span className="absolute top-1.5 left-2 bg-black/50 text-white text-[8px] font-bold px-1 py-0.5 rounded">Ad</span>
          </div>
        )}
      </div>

      {/* Mobile Booking Bar */}
      <div className="fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur-lg border-t border-slate-200 p-3 lg:hidden z-40" data-testid="mobile-booking-bar">
        <div className="flex items-center justify-between max-w-lg mx-auto">
          <div>
            <span className="text-xl font-bold text-slate-900">{cs}{listing.base_price}</span>
            <span className="text-xs text-slate-400 ml-1">/{listing.pricing_model === 'hourly' ? 'hr' : listing.pricing_model}</span>
          </div>
          <Button
            onClick={() => setShowBookingModal(true)}
            className="bg-emerald-500 hover:bg-emerald-600 rounded-xl px-6 h-11 font-semibold text-sm shadow-lg shadow-emerald-500/20"
            data-testid="mobile-book-btn"
          >
            <Lightning size={14} className="mr-1.5" weight="fill" />
            Book {CATEGORY_LABELS[listing?.category] || 'Now'}
          </Button>
        </div>
      </div>

      {/* Booking Modal - Mobile */}
      {showBookingModal && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-end lg:hidden" data-testid="booking-modal">
          <div className="bg-white w-full rounded-t-3xl max-h-[90vh] overflow-y-auto" style={{ animation: 'slideUp 0.3s ease-out' }}>
            <div className="sticky top-0 bg-white border-b border-slate-100 p-4 flex items-center justify-between z-10">
              <h3 className="text-base font-semibold">Book {CATEGORY_LABELS[listing?.category] || 'Space'}</h3>
              <button onClick={() => setShowBookingModal(false)} className="p-2 hover:bg-slate-100 rounded-full" data-testid="close-booking-modal">
                <X size={18} />
              </button>
            </div>
            <div className="p-4">
              <CategoryBookingForm
                listing={listing}
                blockedDates={blockedDates}
                selectedUnit={selectedUnit}
                onClearUnit={() => setSelectedUnit(null)}
                selectedService={selectedService}
                onClearService={() => setSelectedService(null)}
                selectedVariantIndex={selectedVariant}
              />
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes slideUp {
          from { transform: translateY(100%); }
          to { transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default ListingDetail;
